/**
 * shared/protocol/bytes.ts — little-endian byte writer/reader for the binary codecs [NET §6].
 *
 * OWNER: NETCODE agent.
 *
 * - ByteWriter writes into a growable scratch buffer that is reused between messages; `finish()`
 *   copies the written bytes into a fresh ArrayBuffer (callers may transfer it to a Worker).
 * - ByteReader takes any Uint8Array view and honours its byteOffset: Node `Buffer`s from `ws` are
 *   slices of a pooled ArrayBuffer, so `buf.buffer` alone would read garbage [NET §9, pitfall 11].
 * Every integer write wraps to the field width (like a C cast), floats are IEEE f32.
 */

export class ByteWriter {
  private buf: Uint8Array;
  private dv: DataView;
  pos = 0;

  constructor(initial = 1024) {
    this.buf = new Uint8Array(initial);
    this.dv = new DataView(this.buf.buffer);
  }

  reset(): this {
    this.pos = 0;
    return this;
  }

  private ensure(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.pos + n) size *= 2;
    const nb = new Uint8Array(size);
    nb.set(this.buf.subarray(0, this.pos));
    this.buf = nb;
    this.dv = new DataView(nb.buffer);
  }

  u8(v: number): void { this.ensure(1); this.dv.setUint8(this.pos, v & 0xff); this.pos += 1; }
  i8(v: number): void { this.ensure(1); this.dv.setInt8(this.pos, (v << 24) >> 24); this.pos += 1; }
  u16(v: number): void { this.ensure(2); this.dv.setUint16(this.pos, v & 0xffff, true); this.pos += 2; }
  i16(v: number): void { this.ensure(2); this.dv.setInt16(this.pos, (v << 16) >> 16, true); this.pos += 2; }
  u32(v: number): void { this.ensure(4); this.dv.setUint32(this.pos, v >>> 0, true); this.pos += 4; }
  i32(v: number): void { this.ensure(4); this.dv.setInt32(this.pos, v | 0, true); this.pos += 4; }
  f32(v: number): void { this.ensure(4); this.dv.setFloat32(this.pos, v, true); this.pos += 4; }
  bytes(b: Uint8Array): void { this.ensure(b.length); this.buf.set(b, this.pos); this.pos += b.length; }

  /** Overwrite a u8 at an earlier position (counts / lengths written after the payload). */
  patchU8(at: number, v: number): void { this.dv.setUint8(at, v & 0xff); }
  patchU16(at: number, v: number): void { this.dv.setUint16(at, v & 0xffff, true); }

  /** Fresh ArrayBuffer holding exactly the written bytes. */
  finish(): ArrayBuffer {
    return this.buf.slice(0, this.pos).buffer;
  }
}

export class ByteReader {
  private readonly dv: DataView;
  readonly u8a: Uint8Array;
  pos = 0;

  constructor(data: ArrayBuffer | Uint8Array) {
    this.u8a = data instanceof Uint8Array ? data : new Uint8Array(data);
    this.dv = new DataView(this.u8a.buffer, this.u8a.byteOffset, this.u8a.byteLength);
  }

  get length(): number { return this.u8a.byteLength; }
  get remaining(): number { return this.u8a.byteLength - this.pos; }

  private need(n: number): void {
    if (this.pos + n > this.u8a.byteLength) throw new RangeError(`message truncated at byte ${this.pos} (+${n} > ${this.u8a.byteLength})`);
  }

  u8(): number { this.need(1); const v = this.dv.getUint8(this.pos); this.pos += 1; return v; }
  i8(): number { this.need(1); const v = this.dv.getInt8(this.pos); this.pos += 1; return v; }
  u16(): number { this.need(2); const v = this.dv.getUint16(this.pos, true); this.pos += 2; return v; }
  i16(): number { this.need(2); const v = this.dv.getInt16(this.pos, true); this.pos += 2; return v; }
  u32(): number { this.need(4); const v = this.dv.getUint32(this.pos, true); this.pos += 4; return v; }
  i32(): number { this.need(4); const v = this.dv.getInt32(this.pos, true); this.pos += 4; return v; }
  f32(): number { this.need(4); const v = this.dv.getFloat32(this.pos, true); this.pos += 4; return v; }
  bytes(n: number): Uint8Array { this.need(n); const v = this.u8a.subarray(this.pos, this.pos + n); this.pos += n; return v; }
  skip(n: number): void { this.need(n); this.pos += n; }
}

const te = new TextEncoder();
const td = new TextDecoder();
/** Short string: u8 byte length + UTF-8 (truncated to `max` bytes at a code point boundary). */
export function writeStr(w: ByteWriter, s: string, max = 200): void {
  let b = te.encode(s);
  if (b.length > max) {
    let n = max;
    while (n > 0 && (b[n] & 0xc0) === 0x80) n--; // don't cut a UTF-8 sequence
    b = b.subarray(0, n);
  }
  w.u8(b.length);
  w.bytes(b);
}
export function readStr(r: ByteReader): string {
  return td.decode(r.bytes(r.u8()));
}
